import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AlertTriangle, Car, Pencil } from 'lucide-react'
import { PageHero, Button, Input } from '@components/ui'
import { useAuthStore } from '@store/useAuthStore'
import {
  vehicleService,
  type VehicleRevisionAlert,
  type VehicleRevisionPlanBody,
  type RevisionUrgency,
} from '@services/vehicleService'

function urgencyLabel(u: RevisionUrgency): string {
  switch (u) {
    case 'NO_PLAN':
      return 'Sem plano'
    case 'MISSING_LAST':
      return 'Falta última revisão'
    case 'OVERDUE':
      return 'Em atraso'
    case 'DUE_SOON':
      return 'A chegar'
    case 'UPCOMING':
      return 'Futuro'
    default:
      return u
  }
}

function urgencyBadgeClass(u: RevisionUrgency): string {
  switch (u) {
    case 'OVERDUE':
      return 'bg-red-100 text-red-800'
    case 'DUE_SOON':
      return 'bg-amber-100 text-amber-900'
    case 'MISSING_LAST':
      return 'bg-sky-100 text-sky-900'
    case 'NO_PLAN':
      return 'bg-secondary-100 text-secondary-700'
    default:
      return 'bg-emerald-50 text-emerald-800'
  }
}

/** Matrícula só com letras e números (minúsculas), para pesquisar com ou sem hífenes/espaços (ex.: 11AA11 vs 11-AA-11). */
function compactPlateAlnum(plate: string): string {
  return plate.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** Viaturas que precisam de ação: revisão em atraso/a chegar ou faltam dados do plano. */
function isToReviewUrgency(u: RevisionUrgency): boolean {
  return u === 'OVERDUE' || u === 'DUE_SOON' || u === 'MISSING_LAST'
}

type PlanForm = {
  lastRevisionDateStr: string
  lastRevisionKm: string
  revisionIntervalKm: string
  revisionIntervalMonths: string
  currentOdometerKm: string
}

const emptyPlanForm = (): PlanForm => ({
  lastRevisionDateStr: '',
  lastRevisionKm: '',
  revisionIntervalKm: '',
  revisionIntervalMonths: '',
  currentOdometerKm: '',
})

/** Próxima data de alerta quando só há inspeção (sem plano por km). */
const DEFAULT_DATE_INTERVAL_MONTHS = 12

export function RevisionRemindersPage() {
  const { user } = useAuthStore()
  const workshopCompanyId = user?.workshopCompanyId
  const queryClient = useQueryClient()
  const [editingVehicleId, setEditingVehicleId] = useState<number | null>(null)
  const [planForm, setPlanForm] = useState<PlanForm>(emptyPlanForm)
  /** Pré-selecionado «Não»: só data de inspeção; «Sim» mostra os campos de revisão por km. */
  const [wantsRevisionControl, setWantsRevisionControl] = useState(false)
  const [listTab, setListTab] = useState<'toReview' | 'other'>('toReview')
  const [search, setSearch] = useState('')

  const { data: alerts = [], isLoading, error } = useQuery({
    queryKey: ['revision-alerts', workshopCompanyId],
    queryFn: () => vehicleService.getRevisionAlerts(workshopCompanyId ?? undefined),
    enabled: !!workshopCompanyId,
  })

  const { data: editingVehicle } = useQuery({
    queryKey: ['vehicle', editingVehicleId],
    queryFn: () => vehicleService.getById(editingVehicleId!),
    enabled: editingVehicleId != null,
  })

  const counts = useMemo(() => {
    const toReview = alerts.filter((a) => isToReviewUrgency(a.urgency)).length
    return { toReview, other: alerts.length - toReview }
  }, [alerts])

  const displayedRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const tabList = alerts.filter((a) =>
      listTab === 'toReview' ? isToReviewUrgency(a.urgency) : !isToReviewUrgency(a.urgency)
    )
    if (!q) return tabList

    const qPlate = compactPlateAlnum(search)
    const matchesSearch = (row: VehicleRevisionAlert) => {
      const hay = [
        row.registrationPlate,
        row.brand,
        row.model,
        row.summary,
        urgencyLabel(row.urgency),
      ]
        .join(' ')
        .toLowerCase()
      if (hay.includes(q)) return true
      if (qPlate.length > 0 && compactPlateAlnum(row.registrationPlate).includes(qPlate)) return true
      return false
    }

    return tabList.filter(matchesSearch)
  }, [alerts, listTab, search])

  useEffect(() => {
    if (!editingVehicle) return
    setWantsRevisionControl(false)
    setPlanForm({
      lastRevisionDateStr: editingVehicle.lastRevisionDate
        ? String(editingVehicle.lastRevisionDate).slice(0, 10)
        : '',
      lastRevisionKm:
        editingVehicle.lastRevisionKm != null ? String(editingVehicle.lastRevisionKm) : '',
      revisionIntervalKm:
        editingVehicle.revisionIntervalKm != null ? String(editingVehicle.revisionIntervalKm) : '',
      revisionIntervalMonths:
        editingVehicle.revisionIntervalMonths != null ? String(editingVehicle.revisionIntervalMonths) : '',
      currentOdometerKm:
        editingVehicle.currentOdometerKm != null ? String(editingVehicle.currentOdometerKm) : '',
    })
  }, [editingVehicle])

  useEffect(() => {
    if (editingVehicleId == null) {
      setWantsRevisionControl(false)
    }
  }, [editingVehicleId])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editingVehicleId == null) return
      const body: VehicleRevisionPlanBody = {}

      if (!wantsRevisionControl) {
        if (planForm.lastRevisionDateStr.trim()) {
          body.lastRevisionDate = planForm.lastRevisionDateStr.trim()
        }
        body.revisionIntervalMonths = DEFAULT_DATE_INTERVAL_MONTHS
        body.clearKmBasedPlan = true
      } else {
        if (planForm.lastRevisionDateStr.trim()) {
          body.lastRevisionDate = planForm.lastRevisionDateStr.trim()
        }
        if (planForm.lastRevisionKm.trim()) {
          body.lastRevisionKm = parseInt(planForm.lastRevisionKm, 10)
        }
        if (planForm.revisionIntervalKm.trim()) {
          body.revisionIntervalKm = parseInt(planForm.revisionIntervalKm, 10)
        }
        if (planForm.revisionIntervalMonths.trim()) {
          body.revisionIntervalMonths = parseInt(planForm.revisionIntervalMonths, 10)
        }
        if (planForm.currentOdometerKm.trim()) {
          body.currentOdometerKm = parseInt(planForm.currentOdometerKm, 10)
        }
      }

      await vehicleService.patchRevisionPlan(editingVehicleId, body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['revision-alerts', workshopCompanyId])
      await queryClient.invalidateQueries(['vehicle', editingVehicleId])
      setEditingVehicleId(null)
      setPlanForm(emptyPlanForm())
      setWantsRevisionControl(false)
    },
  })

  const openEdit = (row: VehicleRevisionAlert) => {
    setEditingVehicleId(row.vehicleId)
  }

  if (!workshopCompanyId) {
    return (
      <div className="space-y-8">
        <PageHero title="REVISÕES" />
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-800">
          <p>O seu utilizador não está associado a nenhuma oficina. Contacte o administrador.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHero
        title="REVISÕES"
        searchPlaceholder="Pesquisar por matrícula (ex.: 11-AA-11 ou 11AA11), marca, modelo ou estado…"
        searchValue={search}
        onSearchChange={setSearch}
      />

      <section className="rounded-[32px] border border-secondary-200 bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant={listTab === 'toReview' ? 'primary' : 'outline'}
            onClick={() => setListTab('toReview')}
            className="flex items-center gap-2"
          >
            <AlertTriangle className="h-4 w-4" aria-hidden />
            A rever ({counts.toReview})
          </Button>
          <Button
            type="button"
            variant={listTab === 'other' ? 'primary' : 'outline'}
            onClick={() => setListTab('other')}
            className="flex items-center gap-2"
          >
            <Car className="h-4 w-4" aria-hidden />
            Outros carros ({counts.other})
          </Button>
        </div>

        {search.trim() ? (
          <p className="mb-4 text-xs text-secondary-500">
            Com texto na pesquisa, mostram-se viaturas de ambos os separadores (matrícula com ou sem hífenes).
          </p>
        ) : null}

        {error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Não foi possível carregar os alertas. Tente novamente.
          </p>
        ) : null}

        {isLoading ? (
          <p className="text-sm text-secondary-500">A carregar…</p>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-secondary-600">
            Ainda não há viaturas com folhas de obra nesta oficina. Quando criar folhas de obra, as matrículas aparecem
            aqui para configurar o plano de revisão por data e km.
          </p>
        ) : displayedRows.length === 0 ? (
          <p className="text-sm text-secondary-600">
            {search.trim()
              ? 'Nenhum resultado com esta pesquisa.'
              : listTab === 'toReview'
                ? 'Nenhuma viatura a rever de momento. Quando houver revisão em atraso, a chegar ou faltar dados da última revisão, aparecem aqui.'
                : 'Nenhum outro carro nesta lista.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-secondary-200 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  <th className="py-3 pr-3">Matrícula</th>
                  <th className="py-3 pr-3">Viatura</th>
                  <th className="py-3 pr-3">Estado</th>
                  <th className="py-3 pr-3">Resumo</th>
                  <th className="py-3 pr-3">Próx. data</th>
                  <th className="py-3 pr-3">Próx. km</th>
                  <th className="py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row) => (
                  <tr key={row.vehicleId} className="border-b border-secondary-100 last:border-0">
                    <td className="py-3 pr-3 font-medium text-secondary-900">{row.registrationPlate}</td>
                    <td className="py-3 pr-3 text-secondary-700">
                      {row.brand} {row.model}
                    </td>
                    <td className="py-3 pr-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${urgencyBadgeClass(row.urgency)}`}
                      >
                        {urgencyLabel(row.urgency)}
                      </span>
                    </td>
                    <td className="max-w-xs py-3 pr-3 text-secondary-600">{row.summary}</td>
                    <td className="py-3 pr-3 text-secondary-700 whitespace-nowrap">
                      {row.nextRevisionDate
                        ? new Date(row.nextRevisionDate + 'T12:00:00').toLocaleDateString('pt-PT')
                        : '—'}
                      {row.daysUntilDueDate != null && row.daysUntilDueDate >= 0 ? (
                        <span className="ml-1 text-xs text-secondary-400">({row.daysUntilDueDate} d)</span>
                      ) : null}
                    </td>
                    <td className="py-3 pr-3 text-secondary-700 whitespace-nowrap">
                      {row.nextRevisionKm != null ? `${row.nextRevisionKm.toLocaleString('pt-PT')} km` : '—'}
                      {row.kmUntilDue != null && row.kmUntilDue > 0 ? (
                        <span className="ml-1 text-xs text-secondary-400">
                          (faltam {row.kmUntilDue.toLocaleString('pt-PT')})
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3 text-right">
                      <Button type="button" variant="outline" className="gap-1 text-xs" onClick={() => openEdit(row)}>
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                        Plano
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editingVehicleId != null && editingVehicle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-secondary-900">Revisões</h3>
            <p className="mt-0.5 text-sm text-secondary-600">{editingVehicle.registrationPlate}</p>

            <div className="mt-5 space-y-4">
              <label className="block text-sm text-secondary-700">
                <span className="mb-1 block font-medium">Data da última inspeção</span>
                <input
                  type="date"
                  className="input w-full"
                  value={planForm.lastRevisionDateStr}
                  onChange={(e) => setPlanForm((f) => ({ ...f, lastRevisionDateStr: e.target.value }))}
                />
              </label>

              <div className="flex items-center justify-between gap-4 rounded-xl border border-secondary-200 bg-secondary-50/90 px-4 py-3">
                <span className="text-sm font-medium text-secondary-800">Deseja controlar revisões?</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`text-xs tabular-nums ${!wantsRevisionControl ? 'font-semibold text-secondary-900' : 'text-secondary-500'}`}
                  >
                    Não
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={wantsRevisionControl}
                    aria-label={wantsRevisionControl ? 'Sim: controlar revisões por km' : 'Não: apenas inspeção por data'}
                    onClick={() => setWantsRevisionControl((v) => !v)}
                    className={`relative h-8 w-14 shrink-0 rounded-full p-0.5 transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                      wantsRevisionControl ? 'bg-primary-600' : 'bg-secondary-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-7 w-7 rounded-full bg-white shadow transition-all duration-300 ease-out ${
                        wantsRevisionControl ? 'left-[calc(100%-1.875rem)]' : 'left-0.5'
                      }`}
                    />
                  </button>
                  <span
                    className={`text-xs tabular-nums ${wantsRevisionControl ? 'font-semibold text-secondary-900' : 'text-secondary-500'}`}
                  >
                    Sim
                  </span>
                </div>
              </div>

              <div
                className={`overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none ${
                  wantsRevisionControl
                    ? 'max-h-[560px] opacity-100 translate-y-0'
                    : 'max-h-0 opacity-0 -translate-y-1 pointer-events-none'
                }`}
              >
                <div className="space-y-4 border-t border-secondary-100 pt-4">
                  <Input
                    label="Km na última revisão"
                    type="number"
                    value={planForm.lastRevisionKm}
                    onChange={(e) => setPlanForm((f) => ({ ...f, lastRevisionKm: e.target.value }))}
                    placeholder="Ex.: 45000"
                  />
                  <Input
                    label="Intervalo entre revisões (km)"
                    type="number"
                    value={planForm.revisionIntervalKm}
                    onChange={(e) => setPlanForm((f) => ({ ...f, revisionIntervalKm: e.target.value }))}
                    placeholder="Ex.: 15000"
                  />
                  <Input
                    label="Intervalo entre revisões (meses)"
                    type="number"
                    value={planForm.revisionIntervalMonths}
                    onChange={(e) => setPlanForm((f) => ({ ...f, revisionIntervalMonths: e.target.value }))}
                    placeholder="Ex.: 12"
                  />
                  <Input
                    label="Km atual do veículo (opcional)"
                    type="number"
                    value={planForm.currentOdometerKm}
                    onChange={(e) => setPlanForm((f) => ({ ...f, currentOdometerKm: e.target.value }))}
                    placeholder="Atualize quando o carro vier à oficina"
                  />
                </div>
              </div>
            </div>
            {saveMutation.isError ? (
              <p className="mt-3 text-sm text-red-700">Não foi possível guardar. Verifique os valores.</p>
            ) : null}
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditingVehicleId(null)}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={saveMutation.isLoading}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isLoading ? 'A guardar…' : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
